import {PHOTO_DECORATIONS} from './photo-catalog.js';
import {layerScalePatch} from './layer-edit.js';
import {patternPaintedAt,patternLocalPoint,pointInsideNail} from './pattern-hit.js';
import {layerErasePoint,patternPointCovered} from './pattern-erasure.js';

// Match the photographic fallbacks used by the renderer for older saved designs.
const LEGACY_PHOTOS={pearl:'ref-pearl-white',diamond:'ref-diamond-tiny',gem:'ref-diamond-shape-03',teardrop:'ref-diamond-shape-04',bow:'ref-bow-satin-01','silver-bow':'ref-bow-satin-02',ribbon:'ref-bow-satin-05',butterfly:'ref-pearl-shell-08',heart:'ref-glass-pink-01','heart-gem':'ref-diamond-shape-05',shell:'ref-pearl-shell-05',feather:'ref-metal-charm-14',cross:'ref-metal-cross-b-04'};

export function layerLocalBounds(layer) {
  const photo=layer.kind==='charm'&&PHOTO_DECORATIONS.find(item=>item.id===(LEGACY_PHOTOS[layer.type]||layer.type));
  if(photo){const box=photo.presentationBounds||photo.visibleBounds||photo,aspect=box.width/box.height,longest=photo.defaultSize*5;
    const width=aspect>=1?longest:longest*aspect,height=aspect>=1?longest/aspect:longest;return{x:-width/2,y:-height/2,width,height};}
  if(layer.kind==='stroke'&&layer.points?.length){const pad=(layer.strokeWidth||2)/2,x=Math.min(...layer.points.map(p=>p.x))-50-pad,y=Math.min(...layer.points.map(p=>p.y))-75-pad;
    return{x,y,width:Math.max(...layer.points.map(p=>p.x))-50+pad-x,height:Math.max(...layer.points.map(p=>p.y))-75+pad-y};}
  if(layer.kind==='pattern')return layer.stamp?{x:-layer.strokeWidth/2,y:-layer.strokeWidth/2,width:layer.strokeWidth,height:layer.strokeWidth}:{x:-50,y:-75,width:100,height:150};
  const size=layer.kind==='sticker'?44:40;return{x:-size/2,y:-size/2,width:size,height:size};
}
export function layerPoint(layer,point) {
  const angle=(layer.rotation||0)*Math.PI/180,scale=layer.scale||1;
  const x=point.x*(layer.kind==='sticker'?layer.scaleX||1:1),y=point.y*(layer.kind==='sticker'?layer.scaleY||1:1);
  return{x:layer.x+scale*(x*Math.cos(angle)-y*Math.sin(angle)),y:layer.y+scale*(x*Math.sin(angle)+y*Math.cos(angle))};
}
export function normalizeRotation(degrees) {
  if(!Number.isFinite(degrees))return 0;
  return ((degrees+180)%360+360)%360-180;
}
// Work in the nail's SVG coordinates, so a zoomed or tilted canvas does not
// change the gesture. Starting from the saved angle also avoids grip jumps.
export function rotationFromCenter(layer,start,point) {
  const rotation=normalizeRotation(layer.rotation||0);
  if(!start||!point||![layer.x,layer.y,start.x,start.y,point.x,point.y].every(Number.isFinite))return{rotation};
  const sx=start.x-layer.x,sy=start.y-layer.y,dx=point.x-layer.x,dy=point.y-layer.y;
  if(Math.hypot(sx,sy)<.001||Math.hypot(dx,dy)<.001)return{rotation};
  return{rotation:normalizeRotation(rotation+(Math.atan2(dy,dx)-Math.atan2(sy,sx))*180/Math.PI)};
}
export function containsLayerPoint(layer,point) {
  const angle=-(layer.rotation||0)*Math.PI/180,dx=point.x-layer.x,dy=point.y-layer.y,scale=layer.scale||1,b=layerLocalBounds(layer);
  const x=(dx*Math.cos(angle)-dy*Math.sin(angle))/(scale*(layer.kind==='sticker'?layer.scaleX||1:1)),y=(dx*Math.sin(angle)+dy*Math.cos(angle))/(scale*(layer.kind==='sticker'?layer.scaleY||1:1));
  return x>=b.x&&x<=b.x+b.width&&y>=b.y&&y<=b.y+b.height;
}
function strokePaintedAt(layer,point){
  const p=patternLocalPoint(layer,point),points=layer.points||[],radius=(layer.strokeWidth||2)/2;
  if(points.length===1)return Math.hypot(p.x-points[0].x,p.y-points[0].y)<=radius;
  if(layer.brush==='fill'){
    let inside=false;
    for(let i=0,j=points.length-1;i<points.length;j=i++){
      const a=points[i],b=points[j];
      if((a.y>p.y)!==(b.y>p.y)&&p.x<(b.x-a.x)*(p.y-a.y)/(b.y-a.y)+a.x)inside=!inside;
    }
    return inside;
  }
  return points.slice(1).some((b,i)=>{
    const a=points[i],dx=b.x-a.x,dy=b.y-a.y,length=dx*dx+dy*dy;
    const t=length?Math.max(0,Math.min(1,((p.x-a.x)*dx+(p.y-a.y)*dy)/length)):0;
    const width=layer.taper?Math.max(.45,layer.strokeWidth*(1-i/(points.length-1)*.92))/2:radius;
    return Math.hypot(p.x-a.x-t*dx,p.y-a.y-t*dy)<=width;
  });
}
// SVG pattern rectangles receive browser events even in transparent gaps.
// Resolve actual paint from the top down, independent of the previously selected layer.
function frenchPaintedAt(layer,point,svg,hitId){
  if(patternPointCovered(layer,point))return false;
  const shapes=svg?.querySelectorAll('[data-french-shape]');
  if(!shapes?.length)return hitId===layer.id;
  const rootMatrix=svg.getScreenCTM();if(!rootMatrix)return false;
  const screen=new DOMPoint(point.x,point.y).matrixTransform(rootMatrix);
  return [...shapes].some(shape=>{
    const matrix=shape.getScreenCTM();if(!matrix)return false;
    const local=screen.matrixTransform(matrix.inverse());
    return (shape.getAttribute('fill')!=='none'&&shape.isPointInFill(local))||(shape.getAttribute('stroke')&&shape.getAttribute('stroke')!=='none'&&shape.isPointInStroke(local));
  });
}
export function pickLayer(layers,point,hitId,selectedId,svg) {
  if(!point||!Number.isFinite(point.x)||!Number.isFinite(point.y))return undefined;
  const nailPath=svg?.querySelector(':scope > defs > clipPath > path')?.getAttribute('d');
  return [...layers].reverse().find(layer=>{
    if(layer.visible===false||layer.opacity===0)return false;
    if(layer.clip!==false&&!pointInsideNail(point,nailPath))return false;
    if(layer.kind==='french')return frenchPaintedAt(layer,point,svg,hitId);
    if(layer.kind==='pattern')return patternPaintedAt(layer,point,{nailPath});
    if(['sticker','stroke'].includes(layer.kind)&&patternPointCovered(layer,layerErasePoint(layer,point)))return false;
    if(layer.kind==='stroke')return strokePaintedAt(layer,point);
    return containsLayerPoint(layer,point);
  });
}
// Once a repeating pattern is selected, its transparent gaps are part of its
// drag surface too. Directly touched charms/stickers remain selectable unless
// the customer explicitly chose a covered layer with the layer arrows.
export function pickLayerForDrag(layers,point,{hitId,selectedId,pinned=false,svg}={}) {
  const selected=layers.find(layer=>layer.id===selectedId&&layer.visible!==false&&layer.opacity!==0);
  const paint=pickLayer(layers,point,hitId,selectedId,svg);
  const nailPath=svg?.querySelector(':scope > defs > clipPath > path')?.getAttribute('d');
  if(selected?.kind==='pattern'&&!selected.stamp&&point&&Number.isFinite(point.x)&&Number.isFinite(point.y)&&pointInsideNail(point,nailPath)){
    if(pinned||!paint||paint.kind==='pattern')return selected;
  }
  if(pinned&&selected){const pinnedPaint=pickLayer([selected],point,hitId,selectedId,svg);if(pinnedPaint)return pinnedPaint;}
  // A small selected sticker still needs a finger-sized centre to grab. Only
  // extend into empty nail space; real artwork and erased holes keep priority.
  if(!paint&&selected?.kind==='sticker'&&point&&Number.isFinite(point.x)&&Number.isFinite(point.y)&&pointInsideNail(point,nailPath)&&!patternPointCovered(selected,layerErasePoint(selected,point))){
    const matrix=svg?.getScreenCTM();
    if(matrix){
      const dx=point.x-selected.x,dy=point.y-selected.y;
      if(Math.hypot(matrix.a*dx+matrix.c*dy,matrix.b*dx+matrix.d*dy)<=22)return selected;
    }
  }
  return paint;
}
export function resizeFromCorner(layer,corner,start,point) {
  if(layer.kind==='pattern'&&!layer.stamp){
    const dx=start.x-50,dy=start.y-75,ratio=((point.x-50)*dx+(point.y-75)*dy)/(dx*dx+dy*dy||1);
    return layerScalePatch(layer,layer.scale*ratio);
  }
  const b=layerLocalBounds(layer),opposite={x:corner.includes('e')?b.x:b.x+b.width,y:corner.includes('s')?b.y:b.y+b.height};
  const anchor=layerPoint(layer,opposite),dx=start.x-anchor.x,dy=start.y-anchor.y;
  const ratio=((point.x-anchor.x)*dx+(point.y-anchor.y)*dy)/(dx*dx+dy*dy||1),patch=layerScalePatch(layer,layer.scale*ratio);
  if(layer.kind==='sticker'){
    let min=.1,max=20;
    for(const [coordinate,low,high] of [['x',-60,160],['y',-60,210]]){
      const movement=(layer[coordinate]-anchor[coordinate])/(layer.scale||1);if(Math.abs(movement)<1e-10)continue;
      const a=layer.scale+(low-layer[coordinate])/movement,c=layer.scale+(high-layer[coordinate])/movement;
      min=Math.max(min,Math.min(a,c));max=Math.min(max,Math.max(a,c));
    }
    patch.scale=Math.max(min,Math.min(max,patch.scale));
  }
  const newAnchor=layerPoint({...layer,...patch},opposite);
  return{...patch,x:Math.max(-60,Math.min(160,layer.x+anchor.x-newAnchor.x)),y:Math.max(-60,Math.min(210,layer.y+anchor.y-newAnchor.y))};
}

// Edge grips stretch the bitmap along one local axis. Project the drag after
// undoing rotation; motion along the other axis must not change its size.
export function resizeFromAxis(layer,axis,start,point){
  if(layer?.kind!=='sticker'||!['n','e','s','w'].includes(axis)||!start||!point||![start.x,start.y,point.x,point.y].every(Number.isFinite))return{};
  const horizontal=axis==='e'||axis==='w',sign=axis==='e'||axis==='s'?1:-1,key=horizontal?'scaleX':'scaleY';
  const angle=(layer.rotation||0)*Math.PI/180,dx=point.x-start.x,dy=point.y-start.y,scale=layer.scale||1,b=layerLocalBounds(layer);
  const delta=horizontal?dx*Math.cos(angle)+dy*Math.sin(angle):-dx*Math.sin(angle)+dy*Math.cos(angle);
  const old=layer[key]||1,size=horizontal?b.width:b.height;
  const direction=horizontal?{x:Math.cos(angle),y:Math.sin(angle)}:{x:-Math.sin(angle),y:Math.cos(angle)};
  const movement={x:sign*direction.x*scale*size/2,y:sign*direction.y*scale*size/2};
  let min=.1,max=10;
  for(const [coordinate,low,high] of [['x',-60,160],['y',-60,210]]){
    const shift=movement[coordinate];if(Math.abs(shift)<1e-10)continue;
    const a=old+(low-layer[coordinate])/shift,c=old+(high-layer[coordinate])/shift;
    min=Math.max(min,Math.min(a,c));max=Math.min(max,Math.max(a,c));
  }
  const value=Math.max(min,Math.min(max,old+sign*delta/(scale*size)));
  return{[key]:value,x:layer.x+movement.x*(value-old),y:layer.y+movement.y*(value-old)};
}
